<script>
  // @ts-check
  import { lock, unlock } from "tua-body-scroll-lock";

  // eslint-disable-next-line no-unused-vars
  import * as Types from "./globalNavigationTypes";
  import GridContainer from "../../atoms/GridContainer.svelte";
  import { trapFocus } from "../../utilities/accessibility";
  import { isSmallOrMediumBreakpoint } from "../../utilities";
  import { getTheme } from "../../utilities/themes";

  import GlobalNavigationMobileBar from "./GlobalNavigationMobileBar.svelte";
  import GlobalNavigationMobileContent from "./GlobalNavigationMobileContent.svelte";
  import GlobalNavigationAnimationWrapper from "./GlobalNavigationAnimationWrapper.svelte";

  /** @type {import('../../utilities/themes.js').ThemeName} */
  export let theme = "white";

  /** @type {Types.MainNavigation} */
  export let mainNavigation;

  /** @type {string}*/
  export let mainLogoAccessibilityText = "The Obama Foundation – Home";

  /** @type {boolean}*/
  export let isExpanded;

  /** @type {boolean} */
  export let hasScrolledDown = false;

  /** @type {boolean}*/
  export let isSearchPanelVisible;

  /** @type {boolean}*/
  export let delayThemeFadeOnNavBar = false;

  /** @type {boolean}*/
  export let animateNavBarItems = false;

  let displayedMobileSection = undefined;

  let screenWidth;
  $: isMobile = isSmallOrMediumBreakpoint(screenWidth);

  /** @type {import('../../utilities/themes').Theme}*/
  $: currentMobileTheme = getTheme(theme);

  let collapseDisplayedSectionOnSearch = false;

  const handleMobileLabelClicked = (e) => {
    displayedMobileSection = e.detail.displayedMobileSection;

    // if the primary section being clicked does not have any secondary sections, we do not display the secondary menu. We redirect instead
    const clickedSection = mainNavigation.find(
      (section) => section.link.label === e.detail.displayedMobileSection
    );

    if (clickedSection.secondarySections.length === 0 && clickedSection.link.url) {
      displayedMobileSection = undefined;
      window.location.href = clickedSection.link.url;
    }
  };

  const handleHamburgerClick = () => {
    displayedMobileSection = undefined;
    isExpanded = !isExpanded;
  };

  const handleGoBack = () => {
    displayedMobileSection = undefined;
  };

  /** @type {HTMLElement | undefined} */
  let scrollableContainer = undefined;

  /** @type {HTMLElement | undefined} */
  let scrollableContent = undefined;

  $: {
    if (isExpanded && isMobile) {
      lock([scrollableContainer, scrollableContent].filter(Boolean), {
        overflowType: "clip", // overflowType: "clip" is used for layouts with position: sticky
        useGlobalLockState: true
      });
    } else {
      unlock([scrollableContainer, scrollableContent].filter(Boolean));
    }

    // Collapse nav when search panel is open
    if (isSearchPanelVisible && isExpanded) {
      collapseDisplayedSectionOnSearch = true;
      isExpanded = false;
    }

    if (!isSearchPanelVisible) {
      collapseDisplayedSectionOnSearch = false;
    }
  }

  $: navTransitionDelay = isExpanded || isSearchPanelVisible ? "delay-[2000ms]" : "";
</script>

<svelte:window bind:innerWidth={screenWidth} />

<nav
  data-testId="nav-mobile"
  class="cta-md sticky left-0 top-0 z-[--stack-level-global-nav] flex w-full items-center transition-all duration-[300ms] ease-nav-easing {navTransitionDelay} lg:hidden {hasScrolledDown ||
  isExpanded
    ? currentMobileTheme?.backgroundColor
    : 'bg-transparent'}"
  class:shadow-xl={hasScrolledDown}
>
  <GridContainer containerClass="relative h-full w-full">
    {@const triggerElementQuery = ""}
    <div
      use:trapFocus={{
        shouldTrapFocus: isExpanded || isSearchPanelVisible,
        triggerElementQuery
      }}
      class="flex flex-col"
    >
      <GlobalNavigationMobileBar
        bind:isExpanded
        on:hamburgerClick={handleHamburgerClick}
        on:searchClose
        {displayedMobileSection}
        {mainLogoAccessibilityText}
        {theme}
        {hasScrolledDown}
        on:goBack={handleGoBack}
        {isSearchPanelVisible}
        {delayThemeFadeOnNavBar}
        {animateNavBarItems}
      >
        <svelte:fragment slot="utilityNavigation">
          <slot name="utilityNavigation" />
        </svelte:fragment>
      </GlobalNavigationMobileBar>
      {#if isExpanded}
        <GlobalNavigationAnimationWrapper {isMobile} {collapseDisplayedSectionOnSearch} {theme}>
          <svelte:fragment slot="content">
            <GlobalNavigationMobileContent
              bind:scrollableContainer
              bind:scrollableContent
              {mainNavigation}
              {displayedMobileSection}
              {theme}
              bind:isExpanded
              on:mobileLabelClicked={handleMobileLabelClicked}
            />
          </svelte:fragment>
        </GlobalNavigationAnimationWrapper>
      {/if}

      {#if isSearchPanelVisible}
        <GlobalNavigationAnimationWrapper
          {isMobile}
          {isSearchPanelVisible}
          {collapseDisplayedSectionOnSearch}
          {theme}
        >
          <svelte:fragment slot="content">
            <slot name="searchPanel" />
          </svelte:fragment>
        </GlobalNavigationAnimationWrapper>
      {/if}
    </div>
  </GridContainer>
</nav>
